/**
 * Companion-surface auth handoff (Rumio Apps contract v1 §11).
 *
 * Glue used by Login.tsx + AuthCallback.tsx to detect a mobile/extension
 * handoff in the URL, persist it across an OAuth bounce, and call the
 * backend finalize endpoint after auth completes.
 *
 * The companion opens either `<api>/auth/mobile-handoff?...` or
 * `<api>/auth/extension-handoff?...`, which 302s here with the params on
 * the URL. We stash them in sessionStorage so they survive a
 * Google/Apple/Facebook redirect through /auth/callback. The two surfaces
 * differ only in entry-route validation (chromiumapp.org host check +
 * apps.extension_ids allowlist for the extension surface) — the SPA-side
 * flow is identical.
 */

import Session from 'supertokens-web-js/recipe/session';

const STORAGE_KEY = 'rumio_pending_handoff';

export interface HandoffParams {
  app: string;
  redirect_uri: string;
  code_challenge: string;
  code_challenge_method: string;
  surface: 'mobile' | 'extension';
}

export interface FinalizeResponse {
  redirect_url: string;
  expires_in: number;
}

const isHandoffSurface = (v: string): v is HandoffParams['surface'] =>
  v === 'mobile' || v === 'extension';

/**
 * Read handoff params from the URL (or sessionStorage if previously stashed).
 * Persists to sessionStorage on first sight so the params survive a social
 * OAuth round-trip that drops the query string.
 *
 * Accepts both the react-router useSearchParams() result and a stock
 * URLSearchParams so AuthCallback (which mounts before its own
 * useSearchParams resolves) can call it with `new URLSearchParams(window.location.search)`.
 */
export function readHandoffParams(
  params: URLSearchParams,
): HandoffParams | null {
  const fromUrl = parseFromSearch(params);
  if (fromUrl) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(fromUrl));
    } catch {
      // sessionStorage can throw in private-mode Safari — degrade gracefully.
    }
    return fromUrl;
  }
  return readStashedHandoff();
}

/**
 * Look at sessionStorage without touching the URL. Used by AuthCallback
 * after the OAuth bounce, when the original handoff params are no longer
 * in the URL.
 */
export function readStashedHandoff(): HandoffParams | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!isHandoffParams(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Clear the stash. Call after a successful finalize. */
export function clearStashedHandoff(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * POST to /auth/handoff/finalize with the user's bearer JWT.
 *
 * Does NOT go through the shared `api` client because that client pins all
 * requests to `/api/*`. The handoff endpoint lives at the bare `/auth/*`
 * path to keep the URL shape consistent with the public GET entry point.
 */
export async function finalizeCompanionHandoff(
  params: HandoffParams,
): Promise<FinalizeResponse> {
  const token = await Session.getAccessToken();
  if (!token) throw new Error('Not signed in');

  // VITE_API_URL is the API base (e.g., https://api.lsgseeds.com). In dev
  // it's unset and we hit the Vite proxy on the same origin.
  const base = (import.meta.env.VITE_API_URL as string | undefined) ?? '';
  const res = await fetch(`${base}/auth/handoff/finalize`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      app: params.app,
      redirect_uri: params.redirect_uri,
      code_challenge: params.code_challenge,
      code_challenge_method: params.code_challenge_method,
      surface: params.surface,
    }),
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; error_description?: string };
    const msg = body.error_description || body.error || `HTTP ${res.status}`;
    throw new Error(msg);
  }

  const data = (await res.json()) as FinalizeResponse;
  clearStashedHandoff();
  return data;
}

function parseFromSearch(
  params: URLSearchParams,
): HandoffParams | null {
  const surfaceRaw = params.get('handoff');
  if (!surfaceRaw || !isHandoffSurface(surfaceRaw)) return null;

  const app = params.get('app');
  const redirectUri = params.get('redirect_uri');
  const codeChallenge = params.get('code_challenge');
  const codeChallengeMethod = params.get('code_challenge_method');
  if (!app || !redirectUri || !codeChallenge || !codeChallengeMethod) return null;

  return {
    app,
    redirect_uri: redirectUri,
    code_challenge: codeChallenge,
    code_challenge_method: codeChallengeMethod,
    surface: surfaceRaw,
  };
}

function isHandoffParams(v: unknown): v is HandoffParams {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.app === 'string' &&
    typeof o.redirect_uri === 'string' &&
    typeof o.code_challenge === 'string' &&
    typeof o.code_challenge_method === 'string' &&
    (o.surface === 'mobile' || o.surface === 'extension')
  );
}
